// React imports
import React, { useEffect, useRef, useState } from "react";

// MUI imports
import { Autocomplete, FormControl, MenuItem, Select, TextField } from "@mui/material";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Other imports
import PropTypes from "prop-types";
import { useAuth } from "auth/Auth";
import { useCountryCodes } from "hooks/useCountryCodes";

const CONTACT_MODE_OPTIONS = [
    { value: "new", label: "Create new contact" },
    { value: "existing", label: "Use existing contact" },
];

const contactLabel = (c) => {
    const name = [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");
    return c.email ? `${name} (${c.email})` : name;
};

// Fills whatever the contact can provide. Names always follow the contact; other fields only when it has a value.
const buildContactPrefill = (contact, countryCodes) => {
    const updates = {
        first_name: contact.first_name || "",
        middle_name: contact.middle_name || "",
        last_name: contact.last_name || "",
    };
    if (contact.email) updates.email = contact.email;
    if (contact.mailing_address) updates.address = contact.mailing_address;
    if (contact.mailing_country) updates.country = contact.mailing_country;

    let dialCode = contact.dial_code || "";
    let phone = contact.phone || contact.mobile_number || "";
    if (!dialCode && phone.trim().startsWith("+")) {
        const compact = phone.replace(/[^\d+]/g, "");
        const match = [...countryCodes]
            .sort((a, b) => b.dialCode.length - a.dialCode.length)
            .find((c) => compact.startsWith(c.dialCode));
        if (match) {
            dialCode = match.dialCode;
            phone = compact.slice(match.dialCode.length);
        }
    }
    if (dialCode) updates.phoneCountryCode = dialCode;
    if (phone) updates.phone = dialCode ? phone.replace(/\D/g, "") : phone;
    return updates;
};

function TravelerContactSelector({
                                     traveler, primaryAccountId, takenContactIds, onChange, modeError, contactError, onClearErrors, disabled = false,
                                 }) {
    const { axiosInstance } = useAuth();
    const { countryCodes } = useCountryCodes();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadedFor, setLoadedFor] = useState(null);
    const prevAccountRef = useRef(primaryAccountId);

    const isExisting = traveler.contactMode === "existing";
    const selected = options.find((c) => c.id === traveler.contactId) || null;
    // A contact was linked by a save (it was just created) but isn't in the list yet: reload once.
    const missingSelected =
        Boolean(traveler.contactId) && loadedFor === primaryAccountId && !selected;

    // Only the primary traveler's account contacts are selectable.
    useEffect(() => {
        if (!primaryAccountId) {
            setOptions([]);
            setLoadedFor(primaryAccountId);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get("/contacts/", {
                    params: { account_id: primaryAccountId, all: true },
                });
                if (!cancelled) setOptions(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                if (!cancelled) setOptions([]);
            } finally {
                if (!cancelled) {
                    setLoadedFor(primaryAccountId);
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [primaryAccountId, missingSelected]);

    // The primary traveler switched to a different account: this traveler's contact no longer applies.
    useEffect(() => {
        const previous = prevAccountRef.current;
        prevAccountRef.current = primaryAccountId;
        if (previous && previous !== primaryAccountId && traveler.contactMode) {
            onChange({ contactMode: "", contactId: null });
        }
    }, [primaryAccountId]);

    const handleModeChange = (mode) => {
        onClearErrors?.();
        if (mode === "new") {
            // The existing contact's email can't be reused for a new contact, so it has to be typed again.
            onChange({ contactMode: "new", contactId: null, ...(isExisting ? { email: "" } : {}) });
        } else {
            onChange({ contactMode: "existing" });
        }
    };

    const handleContactChange = (contact) => {
        onClearErrors?.();
        if (!contact) {
            onChange({ contactId: null });
            return;
        }
        onChange({ contactId: contact.id, ...buildContactPrefill(contact, countryCodes) });
    };

    const availableOptions = options.filter(
        (c) => c.id === traveler.contactId || !takenContactIds.includes(c.id)
    );

    return (
        <MDBox mb={2.5} p={2} borderRadius="lg" sx={{ bgcolor: "#F7FAFF", border: "1px solid #E3F2FD" }}>
            <MDTypography variant="button" fontWeight="bold" display="block" mb={1.5}>
                Contact
            </MDTypography>

            <MDBox display="flex" flexWrap="wrap" gap={2}>
                <MDBox flex="1 1 220px" minWidth={200} id={`field-${traveler.id}-contactMode`}>
                    <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        Contact{" "}
                        <MDBox component="span" sx={{ color: "#E53935" }}>*</MDBox>
                    </MDTypography>
                    <FormControl fullWidth size="small" error={Boolean(modeError)}>
                        <Select
                            displayEmpty
                            disabled={disabled}
                            value={traveler.contactMode || ""}
                            onChange={(e) => handleModeChange(e.target.value)}
                        >
                            <MenuItem value="" disabled>
                                Select an option
                            </MenuItem>
                            {CONTACT_MODE_OPTIONS.map((opt) => (
                                <MenuItem
                                    key={opt.value}
                                    value={opt.value}
                                    disabled={opt.value === "existing" && !primaryAccountId}
                                >
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {modeError && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                            {typeof modeError === "string" ? modeError : "Select a contact option."}
                        </MDTypography>
                    )}
                </MDBox>

                {isExisting && (
                    <MDBox flex="1 1 320px" minWidth={240} id={`field-${traveler.id}-contactId`}>
                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                            Select contact{" "}
                            <MDBox component="span" sx={{ color: "#E53935" }}>*</MDBox>
                        </MDTypography>
                        <Autocomplete
                            size="small"
                            disabled={disabled}
                            options={availableOptions}
                            loading={loading}
                            value={selected}
                            getOptionLabel={contactLabel}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            noOptionsText="No contacts on this account"
                            onChange={(_, value) => handleContactChange(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search the account's contacts"
                                    error={Boolean(contactError)}
                                />
                            )}
                        />
                        {contactError && (
                            <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                                {typeof contactError === "string" ? contactError : "Select a contact."}
                            </MDTypography>
                        )}
                    </MDBox>
                )}
            </MDBox>

            <MDTypography variant="caption" color="text" display="block" mt={1}>
                {isExisting
                    ? "Details below are filled from the contact. Changes you make will update the contact when the booking is saved."
                    : traveler.contactMode === "new"
                        ? "A new contact will be created under the primary traveler's account. Enter a unique email address: contacts can't share an email."
                        : primaryAccountId
                            ? "Choose whether this traveler is an existing contact of the primary account or a new one."
                            : "Existing contacts are available once the primary traveler uses an existing account."}
            </MDTypography>
        </MDBox>
    );
}

TravelerContactSelector.propTypes = {
    disabled: PropTypes.bool,
    traveler: PropTypes.object.isRequired,
    primaryAccountId: PropTypes.number,
    takenContactIds: PropTypes.arrayOf(PropTypes.number),
    onChange: PropTypes.func.isRequired,
    modeError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    contactError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    onClearErrors: PropTypes.func,
};

TravelerContactSelector.defaultProps = {
    primaryAccountId: null,
    takenContactIds: [],
};

export default TravelerContactSelector;